'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useRouter, usePathname } from 'next/navigation';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  const logout = async () => {
    try {
      setIsAuthenticated(false);
      sessionStorage.removeItem('justAuthenticated');
      await fetch('/api/set-cookie', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: '' }),
        credentials: 'same-origin',
      });
      await signOut();
    } catch {
      setIsAuthenticated(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      const res = await fetch('/api/status', { credentials: 'include' });
      const isAuthed = res.ok;
      setIsAuthenticated(isAuthed);
      return isAuthed;
    } catch {
      setIsAuthenticated(false);
      return false;
    } finally {
      setIsLoading(false);
    }  
  };

  useEffect(() => {
    const protectedRoutes = ['/dashboard', '/admin', '/apply'];
    const isProtected = protectedRoutes.some(route => pathname.startsWith(route));
  
    if (isProtected) {
      checkAuthStatus();
    }
  

    const listener = (data: any) => {
      const { event } = data.payload;
      if (event === 'signedIn') {
        setIsAuthenticated(true);
      } else if (event === 'signedOut') {
        setIsAuthenticated(false);
        sessionStorage.removeItem('justAuthenticated');
      }
    };
    const unsubscribe = Hub.listen('auth', listener);

    return () => {
      unsubscribe();
    };
  }, [pathname]);

  const login = () => setIsAuthenticated(true);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout, checkAuthStatus }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};